/**
 * Edge Data panel — Blender-style per-selection edge-attribute controls.
 *
 * Mirrors Blender's N-panel "Item ▸ Edge Data" section: operates on whichever
 * edges are resolvable from the current selection (edge mode passes through,
 * face mode derives boundary edges, vertex mode classifies the vertex set).
 * Users can mark / clear UV seams and sharp edges, and edit the per-edge
 * subsurf crease weight via a slider (mean of the resolved set).
 *
 * Edit-mode-only and mesh-only.
 */

import { Spline } from "lucide-react";
import { useMemo } from "react";
import { resolveSceneObjectEditSession } from "../../app/editSession.ts";
import { useStore } from "../../app/store.ts";
import { resolveSelectedEdges } from "../../modeling/editSelection.ts";
import { EDGE_SEAM, EDGE_SHARP } from "../../modeling/types.ts";
import { Button } from "../Button.tsx";
import { useDeferredUndo } from "../hooks/useDeferredUndo.ts";
import { CollapsiblePanel, SliderRow } from "../shared.tsx";
import {
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SUBTLE,
  FONT_SIZE_SMALL,
} from "../tokens.ts";

// ---------------------------------------------------------------------------
// Panel shell
// ---------------------------------------------------------------------------

export function EdgeDataPanel({ bare }: { bare?: boolean } = {}) {
  const editMode = useStore((s) => s.editMode);
  const baseObj = useStore((s) =>
    editMode ? s.sceneObjects.get(editMode.objectId) : undefined,
  );
  const activeEditSession = useStore((s) =>
    editMode?.objectId && s.activeEditSession?.objectId === editMode.objectId
      ? s.activeEditSession
      : null,
  );
  const obj = baseObj
    ? resolveSceneObjectEditSession(baseObj, activeEditSession)
    : undefined;

  // Gate: mesh-only, edit-mode-only (mirrors VertexGroupsPanel).
  const unavailable = !editMode || !obj || editMode.isCurve;

  const content = unavailable ? (
    <div
      style={{
        fontSize: FONT_SIZE_SMALL,
        color: COLOR_TEXT_FAINT,
        padding: "4px 0",
      }}
    >
      Enter edit mode on a mesh to edit edge attributes.
    </div>
  ) : (
    <EdgeDataBody objectId={editMode.objectId} />
  );

  if (bare) return content;

  return (
    <CollapsiblePanel
      id="edgeData"
      title="Edge Data"
      icon={<Spline size={11} />}
      defaultCollapsed
    >
      {content}
    </CollapsiblePanel>
  );
}

// ---------------------------------------------------------------------------
// Body — requires a resolved edit session (object + editMode both live)
// ---------------------------------------------------------------------------

function EdgeDataBody({ objectId }: { objectId: string }) {
  const editMode = useStore((s) => s.editMode)!;
  const baseObj = useStore((s) => s.sceneObjects.get(objectId)!);
  const activeEditSession = useStore((s) =>
    s.activeEditSession?.objectId === objectId ? s.activeEditSession : null,
  );
  const topology = resolveSceneObjectEditSession(
    baseObj,
    activeEditSession,
  ).topology;
  const markEdgeSeam = useStore((s) => s.markEdgeSeam);
  const clearEdgeSeam = useStore((s) => s.clearEdgeSeam);
  const markEdgeSharp = useStore((s) => s.markEdgeSharp);
  const clearEdgeSharp = useStore((s) => s.clearEdgeSharp);
  const setEdgeCrease = useStore((s) => s.setEdgeCrease);

  // The store is the sole source of truth for the slider position. During a
  // drag: `onStart` pushes one undo snapshot, every `onChange` tick writes
  // the new weight straight to the store (no local shadow state — that
  // fought the controlled `value` prop and snapped the thumb back), and
  // `onEnd` closes the undo session. This matches how
  // `CurvePropertiesPanel` drives its bevel-depth slider.
  const undo = useDeferredUndo();

  // Resolve the target edge set from the current selection (Blender-style:
  // face mode derives boundary edges, vertex mode classifies the vertex set,
  // edge mode passes through). Recomputed only when the selection or
  // topology changes — both are reference-stable in the store.
  const resolved = useMemo(
    () => resolveSelectedEdges(topology, editMode),
    [topology, editMode],
  );
  const resolvedCount = resolved.size;

  // Collect flag hit counts + crease mean for the resolved set in a single
  // pass so the UI renders against one snapshot.
  const { seamHits, sharpHits, seamMisses, sharpMisses, meanCrease } =
    useMemo(() => {
      let seamHits = 0;
      let sharpHits = 0;
      let creaseSum = 0;
      const { edgeFlags, edgeCreases } = topology;
      for (const ei of resolved) {
        if (edgeFlags[ei] & EDGE_SEAM) seamHits++;
        if (edgeFlags[ei] & EDGE_SHARP) sharpHits++;
        if (edgeCreases) creaseSum += edgeCreases[ei];
      }
      return {
        seamHits,
        sharpHits,
        seamMisses: resolvedCount - seamHits,
        sharpMisses: resolvedCount - sharpHits,
        meanCrease: resolvedCount > 0 ? creaseSum / resolvedCount : 0,
      };
    }, [topology, resolved, resolvedCount]);

  const edgeLabel =
    resolvedCount === 0
      ? "No edges resolved from selection"
      : `${resolvedCount} edge${resolvedCount === 1 ? "" : "s"} targeted`;

  return (
    <>
      <div
        style={{
          fontSize: FONT_SIZE_SMALL,
          color: resolvedCount === 0 ? COLOR_TEXT_FAINT : COLOR_TEXT_SUBTLE,
          padding: "2px 0 6px 0",
        }}
      >
        {edgeLabel}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 4,
        }}
      >
        <ActionButton
          label="Mark Seam"
          title="Tag the targeted edges as UV seams"
          enabled={seamMisses > 0}
          onClick={() => markEdgeSeam(objectId)}
        />
        <ActionButton
          label="Clear Seam"
          title="Remove the UV-seam flag from the targeted edges"
          enabled={seamHits > 0}
          onClick={() => clearEdgeSeam(objectId)}
        />
        <ActionButton
          label="Mark Sharp"
          title="Break auto-smooth across the targeted edges"
          enabled={sharpMisses > 0}
          onClick={() => markEdgeSharp(objectId)}
        />
        <ActionButton
          label="Clear Sharp"
          title="Restore auto-smooth across the targeted edges"
          enabled={sharpHits > 0}
          onClick={() => clearEdgeSharp(objectId)}
        />
      </div>

      {/*
        Crease weight — consumed by the Loop subdivision modifier via Hoppe's
        piecewise-smooth rules (see wasm/src/subsurf.rs). The slider writes
        to every resolved edge on every tick for live preview; one drag
        collapses to a single undo entry via `useDeferredUndo`.
      */}
      {resolvedCount > 0 && (
        <div style={{ marginTop: 8 }}>
          <SliderRow
            label="Crease"
            value={meanCrease}
            min={0}
            max={1}
            step={0.01}
            displayValue={meanCrease.toFixed(2)}
            onStart={undo.pushOnce}
            onChange={(v) => setEdgeCrease(objectId, v)}
            onEnd={undo.reset}
            labelWidth={56}
          />
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Action button — matches VertexGroupsPanel's ActionButton exactly so the
// two edit-mode panels feel like siblings.
// ---------------------------------------------------------------------------

function ActionButton({
  label,
  title,
  enabled,
  onClick,
}: {
  label: string;
  title: string;
  enabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
      title={title}
      variant="secondary"
      size="xs"
      fullWidth
    >
      {label}
    </Button>
  );
}
